import { useState, useEffect, useRef, useMemo } from 'react';
import { Spin, message, Popover } from 'antd';
import { CaretDownOutlined, TranslationOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import logoIcon from '@/assets/images/translatorLogo.svg';

import { trackEvent, TrackingEvent } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';
import { useAuthState } from '@/hooks/auth';
import {
  getLogDetail,
  getShareRoomId,
  subscribeShareLang,
  unsubscribeShareLang,
  heartbeatShareLang,
} from '@/api/subtitles';
import { getShareViewerId } from '@/utils/shareViewerId';
import useShareMqtt from '@/hooks/useShareMqtt';

import { ShareHeader } from './components/ShareHeader';
import { SubtitleStream, type SubtitleStreamItem } from './components/SubtitleStream';
import { WaitingState } from './components/WaitingState';
import { ExpiredState } from './components/ExpiredState';
import { LangSelector, normalizeLang } from './components/LangSelector';
import { useShareViewMode } from './hooks/useShareViewMode';
import { useShareConfig } from './hooks/useShareConfig';
import mergeRealtimeAndHistory from './utils/mergeSubtitles';

import css from './index.module.less';

const GET_APP_URL =
  import.meta.env.VITE_FELO_APP_DOWNLOAD_URL
  || import.meta.env.VITE_FELO_SUBTITLE_HOME_URL
  || 'https://subtitle.felo.me';

/**
 * 把 URL ?lang= 或历史 sessionStorage 里残留的旧版 alias 转成 LANG_OPTIONS 里的 value。
 * 目前只需要处理 'zh' → 'zh_hans'(旧版前端用 'zh' 表示简体)。
 * 其他 lang 已经是稳定 enum name,直接透传。
 */
const resolveLangAlias = (lang: string): string => (lang === 'zh' ? 'zh_hans' : lang);

const LANG_OPTIONS = [
  // 简体中文 value 跟 backend SubtitleLangEnum.zh_hans 对齐,前后端 lang code 统一。
  // 旧版客户端可能传 'zh' 别名,resolveLangAlias 在入口转成 'zh_hans' 兼容。
  { value: 'zh_hans', label: '中文（简体）' },
  { value: 'zh_hant', label: '中文（繁體）' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'ru', label: 'Русский' },
  { value: 'it', label: 'Italiano' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'uk', label: 'Українська' },
  { value: 'cs', label: 'Čeština' },
  { value: 'lo', label: 'ລາວ' },
  { value: 'ar', label: 'العربية' },
  { value: 'pl', label: 'Polski' },
  { value: 'fil', label: 'Filipino' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'pt_br', label: 'Português (Brasil)' },
  { value: 'he', label: 'עברית' },
  { value: 'sv', label: 'Svenska' },
  { value: 'da', label: 'Dansk' },
  { value: 'nb', label: 'Norsk bokmål' },
  { value: 'fi', label: 'Suomi' },
  { value: 'bn', label: 'বাংলা' },
  { value: 'fa', label: 'فارسی' },
  { value: 'ta', label: 'தமிழ்' },
  { value: 'ro', label: 'Română' },
  { value: 'hu', label: 'Magyar' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'bg', label: 'Български' },
  { value: 'ne', label: 'नेपाली' },
  { value: 'my', label: 'မြန်မာ' },
  { value: 'km', label: 'ខ្មែរ' },
];

// 观众"当前查看语言"持久化:按 roomId 隔离存 sessionStorage,刷新后恢复用户上次手动选择,
// 避免刷新被重置成"桌面同款"语言(desktopLangs[0])。隐私模式等 storage 不可用时静默降级。
const VIEW_LANG_STORAGE_PREFIX = 'share-view-lang:';
const readStoredViewLang = (roomId?: string): string => {
  if (!roomId) return '';
  try {
    return sessionStorage.getItem(`${VIEW_LANG_STORAGE_PREFIX}${roomId}`) || '';
  } catch {
    return '';
  }
};
const writeStoredViewLang = (roomId: string | undefined, lang: string): void => {
  if (!roomId || !lang) return;
  try {
    sessionStorage.setItem(`${VIEW_LANG_STORAGE_PREFIX}${roomId}`, lang);
  } catch {
    /* storage 不可用时忽略 */
  }
};

function Share() {
  const { t } = useTranslation();
  const { id: uid, roomId } = useParams();
  const [searchParams] = useSearchParams();

  const authState = useAuthState();
  const visitorUid = authState.user?.uid;

  // 同会话内同 (owner_uid, room_id) 只上报一次, 防止 useQuery 重渲染/refetch 重复打点
  const lastReportedKeyRef = useRef<string | null>(null);

  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [viewMode, setViewMode] = useShareViewMode();
  const [subtitleList, setSubtitleList] = useState<SubtitleStreamItem[]>([]);
  const [isManualScroll, setIsManualScroll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const urlLang = resolveLangAlias(searchParams.get('lang') || '');
  // 查看语言初始值优先级: URL ?lang= > 本会话上次手动选择(sessionStorage) > 空。
  // 为空时再由下方 useEffect 用 desktopLangs[0] 兜底。这样刷新能保留用户上次选择,
  // 而不是每次都被重置成"桌面同款"语言。
  const [selectedLang, setSelectedLang] = useState<string>(urlLang || readStoredViewLang(roomId));
  // MQTT 订阅是否就绪:就绪后才触发 subscribe → history 补翻,避免补翻广播早于本端订阅完成被漏掉。
  // 竞态:观众打开链接立刻 subscribe、后端马上补翻并 MQTT 广播,但本端 MQTT 还没订上 topic → 漏掉,
  // 要等另一个观众加入再触发补翻才收到(实测:开第二个窗口,第一个窗口历史立刻出来)。
  const [mqttReady, setMqttReady] = useState(false);
  // 持有最新 selectedLang,供 MQTT onMessage 闭包读取当前查看语言
  const selectedLangRef = useRef(selectedLang);
  selectedLangRef.current = selectedLang;
  // 多语言译文缓存:key(subtitleId/resultId) → (normalizedLang → text)。
  // desktop 把所有 desktop_langs 译文实时广播给「所有」观众;这里按 lang 全缓存,切语言时
  // 直接从缓存取,不再按当前语言丢弃(否则切到某语言时旧句译文已丢、desktop 又不重推 → 永久卡)。
  const translationsRef = useRef<Map<string, Map<string, string>>>(new Map());

  // 主题
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const onToggleTheme = () => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));

  // 自动滚到底
  useEffect(() => {
    if (!isManualScroll) {
      const scroll = scrollRef.current;
      if (scroll) scroll.scrollTop = scroll.scrollHeight;
    }
  }, [subtitleList, isManualScroll]);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    if (event.deltaY > 0) {
      const isUserScrollUp = scroll.scrollTop < scroll.scrollHeight - scroll.offsetHeight - 2;
      setIsManualScroll(isUserScrollUp);
    } else if (event.deltaY < 0) {
      setIsManualScroll(true);
    }
  };

  // 获取 share room id
  const {
    data: shareData,
    isLoading: shareDataLoading,
    isError: shareDataError,
    refetch: refetchShareData,
  } = useQuery({
    queryKey: ['getShareRoomId', uid, roomId],
    queryFn: () =>
      getShareRoomId({ userId: uid, roomId }).then((res) => res.data.data),
    retry: 2,
    // waiting / 未知状态时 poll 5s, 让主播开会后 15s 内自动切到字幕流页;
    // active 时保留 30s 低频轮询:固定链接(alias)跨多次转录复用,主播结束本场
    // 再开新场会产生新 room,不轮询的话页面会永远钉在旧 room 显示旧内容
    // (query 在旧场结束后返回 waiting → 页面切等待页 → 新场开始后自动进新字幕流);
    // expired / disabled 为真正终态,停止轮询
    refetchInterval: (query) => {
      const st = query.state.data?.status;
      if (st === 'expired' || st === 'disabled') return false;
      if (st === 'active') return 30000;
      return 5000;
    },
  });

  const [shareRoomId, subtitleSegmentId, status] = useMemo(() => {
    if (!shareData) return [undefined, undefined, undefined] as const;
    return [shareData.room_id, shareData.user_subtitle_segment_id, shareData.status] as const;
  }, [shareData]);

  // 固定链接跨场切换:room 变化(上一场 → 新一场)时清空上一场的实时列表与译文缓存,
  // 新场内容由新 segment 的 getLogDetail + 新 room 的 MQTT 流接管。
  // 首次从 undefined → 有值不清(正常初始化)。
  const prevRoomIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // waiting 中转时 shareRoomId 会短暂变 undefined(A → undefined → B),
    // 不能让 undefined 覆盖 prevRef,否则 A→B 的切换被拆成两步、清空判断失效
    if (!shareRoomId) return;
    const prev = prevRoomIdRef.current;
    prevRoomIdRef.current = shareRoomId;
    if (prev && prev !== shareRoomId) {
      setSubtitleList([]);
      translationsRef.current.clear();
    }
  }, [shareRoomId]);

  // 分享配置 (desktop 端在翻的语言、主播预选的观众可选语言、当前订阅集合)
  const {
    desktopLangs,
    allowedLangs,
    audienceLangs,
    audienceFreeChoice,
    refetch: refetchConfig,
  } = useShareConfig(shareRoomId);

  // 默认查看语言: 等 desktopLangs 加载后,若用户没主动指定 URL ?lang=,
  // 也没选过任何语言,就取桌面端基础语言的第一个 (通过 normalize 匹配到 LANG_OPTIONS 里的对应项)
  useEffect(() => {
    if (selectedLang) return;
    if (desktopLangs.length === 0) return;
    const fallback = desktopLangs
      .map((d) => LANG_OPTIONS.find((o) => normalizeLang(o.value) === normalizeLang(d))?.value)
      .find((v) => !!v);
    if (fallback) setSelectedLang(fallback);
  }, [desktopLangs, selectedLang]);

  // 观众当前正在看的语言不再可用时强制切回基础语言并 toast:
  // - 无空槽(全固定): 主播在分享弹窗移除了它(allowed_langs 不再包含)
  // - 有空槽: 主播收紧固定语言/名额后后端裁掉了它(不在 allowed ∪ audience 中)
  // allowed 已含桌面端 + 主播固定(保证集), 有空槽时再并上观众先到先得的订阅。
  // 只在 config 轮询结果变化时检测(5s),避免观众刚 setSelectedLang 时被误切走。
  useEffect(() => {
    if (!selectedLang) return;
    const active = audienceFreeChoice ? [...allowedLangs, ...audienceLangs] : allowedLangs;
    if (active.length === 0) return; // 数据还没加载,不动
    const stillAllowed = active
      .map(normalizeLang)
      .includes(normalizeLang(selectedLang));
    if (stillAllowed) return;
    // selectedLang 已被主播移除,切回 desktop 第一个匹配的
    const fallback = desktopLangs
      .map((d) => LANG_OPTIONS.find((o) => normalizeLang(o.value) === normalizeLang(d))?.value)
      .find((v) => !!v);
    if (fallback && fallback !== selectedLang) {
      setSelectedLang(fallback);
      message.info(
        t('share.lang.kickedToBase', {
          defaultValue: 'Host removed this language. Switched back to the base language.',
        })
      );
    }
  }, [allowedLangs, desktopLangs, audienceLangs, audienceFreeChoice]);

  // 自由模式的主动订阅(含满员换语言 release_lang 与 400/422 toast)。
  // 受控模式不走这里: 点选语言 = 切换查看语言, 下方 effect 在 selectedLang 变化时 subscribe。
  const handleSubscribe = async (lang: string, releaseLang?: string): Promise<boolean> => {
    if (!shareRoomId) return false;
    try {
      await subscribeShareLang({
        room_id: shareRoomId,
        lang,
        viewer_id: getShareViewerId(),
        ...(releaseLang ? { release_lang: releaseLang } : {}),
      });
      refetchConfig();
      return true;
    } catch (e: any) {
      const httpStatus = e?.response?.status;
      const bizCode = e?.response?.data?.status ?? e?.response?.data?.code;
      if (httpStatus === 400 || bizCode === 400 || bizCode === 422) {
        message.warning(
          releaseLang
            ? t('share.lang.swapFailedToast', {
              defaultValue: 'Cannot switch: other viewers are still using your current language',
            })
            : t('share.lang.limitToast', { defaultValue: 'Cannot add more languages' }),
        );
      }
      return false;
    }
  };

  // 用户主动切换查看语言:持久化到 sessionStorage,刷新后由初始化逻辑恢复。
  // 仅在用户主动选择时写入;desktopLangs[0] 兜底不走这里,不污染"用户意图"。
  const handleSelectLang = (lang: string) => {
    setSelectedLang(lang);
    writeStoredViewLang(roomId, lang);
  };

  // 当前 selectedLang 是不是 desktop 已经在翻的 (按 normalize 比对)
  // — 是的话不需要走 audience set 链路 (subscribe/heartbeat/unsub),否则会被算成 extra
  // 触发 multiplier 错误 (主播切单/双向、新旧 lang 无重叠时 audience 残留旧 lang)
  const isDesktopLang = useMemo(() => {
    if (!selectedLang) return false;
    const ds = new Set(desktopLangs.map(normalizeLang));
    return ds.has(normalizeLang(selectedLang));
  }, [selectedLang, desktopLangs]);

  // 进入/切换查看语言时,都向后端发一次 subscribe —— 关键作用是触发该语言的 history 补翻,
  // 让「中途加入」的观众也能拿到加入前的历史译文。
  // - extra 语言:加入 audience set + 触发补翻
  // - desktop 语言:total = desktop ∪ audience 去重、不增 cap;同样触发补翻
  //   (此前 desktop 语言走 unsubscribe、不补翻 → 中途加入看 desktop 语言时历史永久卡/空)
  // 倍率计费已取消,desktop 语言进 audience 不再有副作用;无心跳约 2 分钟自动过期。
  useEffect(() => {
    // 必须等 mqttReady:否则 subscribe 触发的 history 补翻广播会早于本端 MQTT 订阅、被漏掉。
    if (!shareRoomId || !selectedLang || !mqttReady) return;
    subscribeShareLang({ room_id: shareRoomId, lang: selectedLang, viewer_id: getShareViewerId() }).catch(() => {});
  }, [shareRoomId, selectedLang, mqttReady]);

  // 2 分钟心跳刷新 TTL (desktop 已翻的不需要心跳)
  useEffect(() => {
    if (!shareRoomId || !selectedLang || isDesktopLang) return undefined;
    const id = window.setInterval(() => {
      heartbeatShareLang({ room_id: shareRoomId, lang: selectedLang, viewer_id: getShareViewerId() }).catch(() => {});
    }, 120000);
    return () => window.clearInterval(id);
  }, [shareRoomId, selectedLang, isDesktopLang]);

  // 切换查看语言/离开页面组件时,退订自己对旧语言的订阅(观众粒度,只删自己的记录,
  // 不影响同语言的其他观众)。最后一个订阅者退订后,后端立即释放该语言的名额。
  // 用 effect cleanup 实现:reload/关标签页不会执行 React cleanup → 不会重现当年
  // beforeunload 误退订导致的刷新掉语言(4→3);彻底关页由后端 5 分钟 TTL 兜底回收。
  useEffect(() => {
    if (!shareRoomId || !selectedLang || isDesktopLang) return undefined;
    const roomId = shareRoomId;
    const lang = selectedLang;
    return () => {
      unsubscribeShareLang({ room_id: roomId, lang, viewer_id: getShareViewerId() }).catch(() => {});
    };
  }, [shareRoomId, selectedLang, isDesktopLang]);

  // 仍不在 beforeunload 主动退订:
  // 刷新(reload)也会触发 beforeunload,退订会造成"先释放再重订"的空窗,极端情况下名额被
  // 其他观众抢走 → 刷新后语言丢失(历史 bug 4→3 的成因之一)。关标签页由后端 TTL 兜底即可。

  // 页面重新可见/获焦时补订阅（防心跳丢失导致 TTL 过期；desktop 已翻的不补）
  useEffect(() => {
    if (!shareRoomId || !selectedLang || isDesktopLang) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        subscribeShareLang({ room_id: shareRoomId, lang: selectedLang, viewer_id: getShareViewerId() }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [shareRoomId, selectedLang]);

  // 页面恢复可见时补拉历史字幕:后台期间 WebSocket 常被浏览器挂起,MQTT 断线
  // 窗口的消息 broker 不保留(clean session),不补拉会缺一段直到手动刷新。
  // 独立于上面的订阅 effect(不受 isDesktopLang 限制,所有观众都需要补拉);
  // react-query 的 refetch 自带并发去重,重复触发无害。
  useEffect(() => {
    const onVisibleRefetch = () => {
      if (document.visibilityState === 'visible') {
        logDetailRefetch();
      }
    };
    document.addEventListener('visibilitychange', onVisibleRefetch);
    window.addEventListener('focus', onVisibleRefetch);
    return () => {
      document.removeEventListener('visibilitychange', onVisibleRefetch);
      window.removeEventListener('focus', onVisibleRefetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // share_view 埋点：仅在业务真正成功（同时拿到 roomId 与 segmentId）且 auth 状态确定后上报一次
  useEffect(() => {
    if (authState.loading) return;
    if (!uid || !shareRoomId || !subtitleSegmentId) return;

    const key = `${uid}:${shareRoomId}`;
    if (lastReportedKeyRef.current === key) return;
    lastReportedKeyRef.current = key;

    // PRD #4: 会中/会后两套 URL 分别统计
    const urlType = status === 'active' ? 'in_session' : 'post_session';
    trackEvent(TrackingEvent.SHARE_VIEW, {
      owner_uid: uid,
      room_id: shareRoomId,
      url_type: urlType,
    });
    reportCustomLog(
      'share_view',
      visitorUid,
      { owner_uid: uid, room_id: shareRoomId, url_type: urlType },
      'Auto',
    );
  }, [uid, shareRoomId, subtitleSegmentId, visitorUid, authState.loading]);

  // 拉日志详情
  const {
    data: logDetailData,
    isLoading: logDetailLoading,
    isError: logDetailError,
    refetch: logDetailRefetch,
    error: detailError,
  } = useQuery({
    queryKey: ['getLogDetail', subtitleSegmentId],
    queryFn: () => {
      if (!subtitleSegmentId) return Promise.resolve(null);
      return getLogDetail(subtitleSegmentId).then((res) => res.data.data);
    },
    retry: 2,
    enabled: !!subtitleSegmentId && status === 'active',
  });

  // 把 logDetail 的 subtitle_list 注入本地 list,并与 MQTT 累积的实时条目合并。
  // 合并/去重/按需清译文的核心逻辑抽到 mergeRealtimeAndHistory(纯函数,见 utils/mergeSubtitles)。
  useEffect(() => {
    if (!logDetailData) return;
    // 历史字幕的 t.lang 跟观众 selectedLang 都按 normalizeLang 比对,
    // 防止 zh_hans / zh / zh-CN 这种"同一种语言不同写法"导致匹配不上、
    // SubtitleStream 进 noTranslationAtAll 分支 fallback 显示原文。
    const normalized = normalizeLang(selectedLang);
    const historicalItems: SubtitleStreamItem[] = logDetailData.subtitle_list.map(
      (item) => {
        const sid = item.subtitle_id;
        // 译文优先级:logDetail 写库的 > 多语言缓存(MQTT 广播 / history 补翻,按当前语言取)
        // 方案 A: 历史句译文严格只取「选中语言」的写库译文;取不到即视为该句无选中语言译文
        // (原文即选中语言, 或后端未译该语言)→ 只显示原文, 不再 fallback 到其它缓存,
        // 避免切换语言后残留旧语言(如切日语后仍显示切换前的英文)译文。
        const fromLog = item.subtitle_translation_list?.find(
          (t) => normalizeLang(t.lang) === normalized,
        )?.text;
        // 增强: 译文与原文相同(原文即选中语言, 或纠错后源文被回写成同语言条目)→ 视为无需译文,
        // 只显示原文, 不在译文列重复原文。
        const translateText = fromLog && fromLog !== item.text ? fromLog : '';
        return {
          resultId: sid,
          subtitleId: sid,
          originalText: item.text,
          translateText,
          // 历史条目:后端不会补推目标语言翻译,无翻译时不显示"翻译中..."
          isHistorical: true,
        };
      },
    );

    setSubtitleList((prev) =>
      mergeRealtimeAndHistory(prev, historicalItems, normalized, translationsRef.current),
    );
  }, [logDetailData, selectedLang]);

  // MQTT 实时增量
  useShareMqtt({
    roomId: shareRoomId,
    // 断线重连成功 → 补拉历史字幕,填补断线窗口丢失的实时消息
    // (弱网/手机锁屏/切后台挂起 WebSocket 是常态,不补拉会缺一段直到手动刷新)
    onReconnected: () => {
      logDetailRefetch();
    },
    onMessage: (msg: API.TranslateList) => {
      const d = msg.data as {
        resultId: string;
        subtitleId?: string;
        originalText?: string;
        translateText?: string;
        translateLang?: string;
        translate_lang?: string;
        originalLang?: string;
      };
      if (msg.type === 'MEET_SUBTITLE_TEXT') {
        // 原文消息:resultId(识别引擎)+ originalText,无 subtitleId
        setSubtitleList((prev) => {
          const next = [...prev];
          const idx = next.findIndex((it) => it.resultId === d.resultId);
          if (idx > -1) {
            next[idx] = { ...next[idx], originalText: d.originalText, originalLang: d.originalLang };
          } else {
            next.push({ resultId: d.resultId, originalText: d.originalText, originalLang: d.originalLang });
          }
          return next;
        });
        return;
      }
      // 译文消息(所有语言都收):按 lang 写入多语言缓存,双 key(subtitleId + resultId)
      const sid = d.subtitleId;
      const lang = normalizeLang(d.translateLang || d.translate_lang || '');
      const text = d.translateText || '';
      const rec = (k?: string) => {
        if (!k) return;
        let m = translationsRef.current.get(k);
        if (!m) {
          m = new Map();
          translationsRef.current.set(k, m);
        }
        m.set(lang, text);
      };
      rec(sid);
      rec(d.resultId);
      // 条目译文按「当前查看语言」从缓存取(刚写入的若正是当前语言即命中)
      const cur = normalizeLang(selectedLangRef.current);
      const pick = (it: SubtitleStreamItem): string => {
        const bySid = it.subtitleId
          ? translationsRef.current.get(it.subtitleId)?.get(cur)
          : undefined;
        return bySid || translationsRef.current.get(it.resultId)?.get(cur) || '';
      };
      setSubtitleList((prev) => {
        const next = [...prev];
        // subtitleId 优先对齐(history 补翻 resultId=subtitleId 也能命中),回退 resultId
        let idx = -1;
        if (sid) {
          idx = next.findIndex(
            (it) => (it.subtitleId && it.subtitleId === sid) || it.resultId === sid,
          );
        }
        if (idx < 0) idx = next.findIndex((it) => it.resultId === d.resultId);
        if (idx > -1) {
          const merged = { ...next[idx], subtitleId: sid ?? next[idx].subtitleId };
          merged.translateText = pick(merged);
          next[idx] = merged;
        } else if (lang === cur) {
          // 当前语言、且条目还不存在(译文先于原文到达)→ 新建
          next.push({ resultId: d.resultId, subtitleId: sid, translateText: text });
        }
        // 非当前语言且无对应条目:只进缓存(已 rec),等切到该语言时由 merge effect 取出
        return next;
      });
    },
    onLoaded: () => {
      // MQTT 已连上并订好 topic → 此刻起 subscribe 触发的补翻广播必被本端收到,放行 subscribe。
      setMqttReady(true);
      logDetailRefetch();
    },
  });

  // === 渲染 ===
  if (shareDataLoading || logDetailLoading) {
    return (
      <div className={css.shareContainer}>
        <div className={css.share}>
          <div className={css.pageTitle}>
            <div className={css.largeLogo}>
              <img src={logoIcon} alt="Felo Logo" />
              <span>{t('menuSubtitles')}</span>
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (shareDataError || logDetailError) {
    return (
      <div className={css.shareContainer}>
        <div className={css.share}>
          <ShareHeader theme={theme} onToggleTheme={onToggleTheme} />
          <div className="flex items-center justify-center flex-1">
            <span className="text-red-500">{t('share.error')}</span>
            {(detailError as any)?.response?.data?.message ? (
              <span className="text-red-500">({(detailError as any).response.data.message})</span>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // status 分支:waiting / expired / disabled 不走 active 路径
  if (status === 'waiting') {
    return (
      <div className={css.shareContainer}>
        <div className={css.share}>
          <ShareHeader theme={theme} onToggleTheme={onToggleTheme} />
          <WaitingState
            userName={shareData?.user_name}
            userAvatar={shareData?.user_avatar}
            onRetry={() => refetchShareData()}
          />
        </div>
      </div>
    );
  }

  if (status === 'expired' || status === 'disabled') {
    return (
      <div className={css.shareContainer}>
        <div className={css.share}>
          <ShareHeader theme={theme} onToggleTheme={onToggleTheme} />
          <ExpiredState />
        </div>
      </div>
    );
  }

  const headerRight = (
    <div className={css.headerRight}>
      <Popover
        trigger="click"
        placement="bottomRight"
        arrow={false}
        overlayInnerStyle={{ width: 280, padding: 8 }}
        // 渲染在 trigger 父容器内,而不是 portal 到 body —— 避免 antd 给
        // body 加 padding-right 时(为防止滚动条隐藏)出现页面横向抖动
        getPopupContainer={(t) => t.parentElement || document.body}
        content={
          <LangSelector
            allOptions={LANG_OPTIONS}
            desktopLangs={desktopLangs}
            allowedLangs={allowedLangs}
            audienceLangs={audienceLangs}
            freeChoice={audienceFreeChoice}
            selectedLang={selectedLang}
            onSelectLang={handleSelectLang}
            onSubscribe={handleSubscribe}
          />
        }
      >
        <button type="button" className={css.langChip}>
          <TranslationOutlined style={{ fontSize: 14, marginRight: 6, color: '#fff', flexShrink: 0 }} />
          <span className={css.langChipLabel}>
            {LANG_OPTIONS.find((o) => o.value === selectedLang)?.label
              ?? t('share.lang.select', { defaultValue: 'Language' })}
          </span>
          <CaretDownOutlined style={{ fontSize: 10, marginLeft: 6, color: '#fff', flexShrink: 0 }} />
        </button>
      </Popover>
      <button
        type="button"
        className={css.headerBtn}
        onClick={() => setViewMode(viewMode === 'both' ? 'translation' : 'both')}
      >
        {viewMode === 'both' ? t('share.hideOriginal') : t('share.showOriginal')}
      </button>
      <a
        className={css.headerBtn}
        href={GET_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          // PRD #5: Get App 抓分享来源, 让 BI 看 share_page → app 转化率
          trackEvent(TrackingEvent.GET_APP_CLICK, {
            source: 'share_page',
            owner_uid: uid ?? '',
            room_id: shareRoomId ?? '',
          });
          reportCustomLog(
            'get_app_click',
            visitorUid,
            { source: 'share_page', owner_uid: uid ?? '', room_id: shareRoomId ?? '' },
            'Manual',
          );
        }}
      >
        {t('share.getApp')}
      </a>
    </div>
  );

  return (
    <div className={css.shareContainer}>
      <div className={css.share}>
        <ShareHeader
          theme={theme}
          onToggleTheme={onToggleTheme}
          rightExtra={headerRight}
        />

        <div className={css.subtitlesdetail} ref={scrollRef} onWheel={handleWheel}>
          <main className={css.body}>
            <SubtitleStream items={subtitleList} viewMode={viewMode} selectedLang={selectedLang} />
          </main>
        </div>

      </div>
    </div>
  );
}

export default Share;
